import { useEffect, useMemo, useState } from 'react';
import { listSpaces, type SpaceListItem } from '../api/auth';
import { useAuth } from '../auth/AuthContext';
import { spaceSwitchUrl } from '../shell/space-switch';
import './spaces.css';

const SPACE_DESCRIPTION: Record<string, string> = {
  rondo: 'Uživatelé, produkty, finance a marketing na jednom místě.',
  affilmaster: 'Affiliate síť, klienti, výkon kampaní a vyúčtování.',
  affilplatform: 'Leady, obchodní pipeline a jednoduché CRM pro tým.',
  dmwk: 'Agenturní projekty, týmová agenda a každodenní provoz.',
};

function SpaceTileIcon({ space }: { space: SpaceListItem }) {
  const [failed, setFailed] = useState(false);
  if (space.slug && !failed) {
    return <img className="space-icon" src={`/assets/spaces/${space.slug}.png`} alt={space.name} onError={() => setFailed(true)} />;
  }
  return (
    <span className="space-icon space-icon--letter">{(space.name ?? 'S').charAt(0).toUpperCase()}</span>
  );
}

function initials(firstName: string | null | undefined, lastName: string | null | undefined, email: string | null | undefined) {
  const fromName = `${firstName?.charAt(0) ?? ''}${lastName?.charAt(0) ?? ''}`.trim();
  return (fromName || email?.charAt(0) || 'P').toUpperCase();
}

export function SpacesLanding({ initialSpaces }: { initialSpaces?: SpaceListItem[] }) {
  const { me, logout } = useAuth();
  const [spaces, setSpaces] = useState<SpaceListItem[] | null>(initialSpaces ?? null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (initialSpaces !== undefined) {
      setSpaces(initialSpaces);
      return;
    }
    let cancelled = false;
    listSpaces()
      .then((rows) => { if (!cancelled) setSpaces(rows); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [initialSpaces]);

  const visibleSpaces = useMemo(
    () => (spaces ?? []).filter((space) => space.status === 'active'),
    [spaces],
  );

  const loc = {
    protocol: window.location.protocol,
    hostname: window.location.hostname,
    port: window.location.port,
  };

  const displayName = me?.displayName || [me?.firstName, me?.lastName].filter(Boolean).join(' ') || me?.email || 'Přihlášený uživatel';
  const userInitials = initials(me?.firstName, me?.lastName, me?.email);

  return (
    <main className="spaces-screen">
      <div className="spaces-shell">
        <header className="spaces-topbar">
          <img className="spaces-platform-logo" src="/assets/platform1-logo.png" alt="Platform 1" />
          <div className="spaces-user">
            <span className="spaces-user-avatar" aria-hidden="true">
              {me?.avatarUrl ? <img src={me.avatarUrl} alt="" /> : userInitials}
            </span>
            <span className="spaces-user-copy"><strong>{displayName}</strong><small>{me?.email}</small></span>
            <button type="button" className="spaces-logout" onClick={() => void logout()}>Odhlásit</button>
          </div>
        </header>

        <section className="spaces-content" aria-labelledby="spaces-title">
          <div className="spaces-header">
            <span className="spaces-eyebrow">VAŠE PROSTŘEDÍ</span>
            <h1 className="spaces-greeting" id="spaces-title">Kam chcete pokračovat?</h1>
            <p className="spaces-subtitle">Vyberte Space. Kdykoliv jej pak můžete změnit přímo v horním menu Platform 1.</p>
          </div>

          {failed ? (
            <div className="spaces-state" role="alert"><strong>Spaces se nepodařilo načíst.</strong><span>Obnovte stránku a zkuste to znovu.</span></div>
          ) : spaces === null ? (
            <div className="spaces-loading" aria-label="Načítám Spaces"><span /><span /><span /><span /></div>
          ) : visibleSpaces.length === 0 ? (
            <div className="spaces-state"><strong>Nemáte dostupnou žádnou Space.</strong><span>Požádejte správce Platform 1 o přístup.</span></div>
          ) : (
            <div className="spaces-grid">
              {visibleSpaces.map((space) => {
                const url = spaceSwitchUrl(space.subdomain, null, loc);
                const description = SPACE_DESCRIPTION[space.slug] ?? 'Otevřít pracovní prostředí Platform 1.';
                const body = (
                  <>
                    <SpaceTileIcon space={space} />
                    <span className="space-copy">
                      <span className="space-kicker">SPACE</span>
                      <h2 className="space-name">{space.name}</h2>
                      <span className="space-description">{description}</span>
                    </span>
                    <span className="space-open" aria-hidden="true"><span>Otevřít</span></span>
                  </>
                );
                return url ? (
                  <a key={space.id} className={`space-tile space-tile--${space.slug}`} href={url} aria-label={`Otevřít ${space.name}`}>
                    {body}
                  </a>
                ) : (
                  <div key={space.id} className={`space-tile space-tile--${space.slug} disabled`} title="Tato Space nemá nastavenou adresu">
                    {body}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        <footer className="spaces-footer"><span>Platform 1</span><span aria-hidden="true">·</span><span>DMWK</span></footer>
      </div>
    </main>
  );
}
